import { registerLocaleVariant } from "../../registry";
import { makeId } from "../../helpers";
import type { ModuleBody } from "../../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frModule(
  slug: string,
  variant: {
    title: string;
    subtitle?: string;
    body: Partial<ModuleBody>;
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── Assessment & testing ───────────────────────────────────────────

frModule("module-female-hormone-baseline", {
  title: "Bilan de fertilité (Elle)",
  subtitle:
    "Panel hormonal complet et imagerie pour cerner ton point de départ",
  body: {
    includes: [
      "Panel hormonal (AMH, FSH, LH, œstradiol) prélevé au 2e ou 3e jour du cycle",
      "Bilan thyroïdien (TSH, T4 libre)",
      "Taux de vitamine D",
      "Échographie endovaginale avec comptage des follicules antraux (CFA)",
      "Restitution des résultats avec une infirmière spécialisée en fertilité",
    ],
    trackableMarkers: [
      {
        name: "AMH",
        expectedToChange: false,
        expectation:
          "L'AMH reflète ton stock folliculaire et décline généralement avec l'âge. C'est ton point de référence pour planifier la suite.",
        retestInterval: "6 mois",
      },
      {
        name: "FSH",
        expectedToChange: false,
        expectation:
          "La FSH au 3e jour donne une indication sur la fonction ovarienne. Une tendance à la hausse est un signal pour agir plus tôt.",
        retestInterval: "3 à 6 mois",
      },
      {
        name: "TSH",
        expectedToChange: true,
        expectation:
          "Si elle est élevée, un traitement permet de ramener la TSH sous la cible fertilité de 2,5. Généralement corrigée en 6 à 8 semaines.",
        retestInterval: "6 à 8 semaines après le début du traitement",
      },
      {
        name: "Vitamine D",
        expectedToChange: true,
        expectation:
          "Le déficit est courant et se corrige avec des compléments. Cible : > 40 ng/mL.",
        retestInterval: "8 semaines",
      },
      {
        name: "CFA",
        expectedToChange: false,
        expectation:
          "Le comptage des follicules antraux varie de 2 à 5 follicules d'un cycle à l'autre. Utile comme référence, pas comme indicateur d'optimisation.",
      },
    ],
    steps: [
      {
        title: "Programmer les analyses",
        description:
          "La prise de sang doit être réalisée au 2e ou 3e jour du cycle pour que la FSH, la LH et l'œstradiol soient exploitables. On t'aide à trouver un créneau pratique.",
      },
      {
        title: "Échographie",
        description:
          "Échographie endovaginale pour compter les follicules antraux et vérifier la présence d'éventuelles anomalies structurelles (fibromes, polypes, kystes).",
      },
      {
        title: "Restitution des résultats",
        description:
          "Appel de 30 minutes avec une infirmière spécialisée en fertilité pour décrypter tes résultats et discuter des prochaines étapes.",
      },
    ],
  },
});

frModule("module-male-fertility-baseline", {
  title: "Bilan de fertilité (Lui)",
  subtitle: "Spermogramme pour poser les chiffres de départ",
  body: {
    includes: [
      "Spermogramme complet (numération, mobilité, morphologie, volume)",
      "Consignes d'abstinence de 2 à 5 jours avant le prélèvement",
      "Interprétation des résultats avec un·e spécialiste de la fertilité",
    ],
    trackableMarkers: [
      {
        name: "Numération",
        expectedToChange: true,
        expectation:
          "Numération de référence en millions/mL. Réagit nettement aux changements de mode de vie et aux compléments sur 90 jours.",
        retestInterval: "90 jours",
      },
      {
        name: "Mobilité",
        expectedToChange: true,
        expectation:
          "Pourcentage de spermatozoïdes à mobilité progressive. C'est souvent le premier paramètre qui s'améliore.",
        retestInterval: "90 jours",
      },
      {
        name: "Morphologie",
        expectedToChange: true,
        expectation:
          "Pourcentage de formes normales (critères stricts de Kruger). Le paramètre le moins réactif, mais qui peut évoluer modérément.",
        retestInterval: "90 jours",
      },
    ],
    steps: [
      {
        title: "Période d'abstinence",
        description:
          "2 à 5 jours d'abstinence avant le prélèvement pour standardiser les résultats.",
      },
      {
        title: "Recueil du prélèvement",
        description:
          "Au laboratoire ou avec un kit à domicile. Résultats généralement disponibles sous 2 à 3 jours ouvrés.",
      },
      {
        title: "Restitution",
        description:
          "Comprendre ta numération, ta mobilité et ta morphologie — et ce qu'elles impliquent pour la suite.",
      },
    ],
  },
});

frModule("module-comprehensive-wellness", {
  title: "Bilan de santé complet",
  subtitle:
    "Plus de 100 biomarqueurs en une seule prise de sang — métabolique, hormonal, inflammatoire, auto-immun, nutritionnel et plus",
  body: {
    includes: [
      "Panel hormonal complet — FSH, LH, œstradiol, prolactine, DHEA-S, testostérone totale et libre, SHBG, cortisol",
      "Thyroïde complète — TSH, T3 libre, T4 libre, anticorps anti-TPO, anticorps anti-thyroglobuline",
      "Santé métabolique — glycémie à jeun, insuline à jeun, HbA1c, acide urique, index triglycérides-glucose",
      "Bilan lipidique avancé — cholestérol total, LDL, HDL, triglycérides, ApoB, lipoprotéine(a), taille et nombre de particules LDL",
      "Inflammation — CRP ultrasensible, homocystéine, VS, ferritine",
      "Nutriments — vitamine D, B12, folates, bilan martial, zinc, magnésium, index oméga-3",
      "Fonction hépatique — ALAT, ASAT, GGT, bilirubine, albumine, phosphatases alcalines",
      "Fonction rénale — créatinine, urée, DFG estimé, électrolytes",
      "Numération formule sanguine — globules rouges, globules blancs, plaquettes, hémoglobine, hématocrite, formule",
      "Dépistage auto-immun — AAN, anticorps antithyroïdiens",
      "IGF-1 et autres marqueurs de longévité",
      "Anticorps antiphospholipides — anticardiolipine, anticoagulant lupique, anti-bêta-2-glycoprotéine (dépistage en cas de fausses couches à répétition)",
      "Génétique de la coagulation — Facteur V Leiden, mutation du gène de la prothrombine, MTHFR C677T/A1298C",
      "Progestérone en milieu de phase lutéale (vers le 21e jour) pour confirmer l'ovulation",
      "Dépistage des maladies infectieuses — immunité rubéole, varicelle, hépatites B/C, VIH, CMV",
      "Synthèse des résultats avec un·e praticien·ne spécialisé·e en fertilité, qui relie les 100+ marqueurs à ton projet reproductif",
    ],
    trackableMarkers: [
      {
        name: "Insuline à jeun",
        expectedToChange: true,
        expectation:
          "L'insulinorésistance est fréquente en cas de PMOS (ex SOPK) et impacte directement la qualité ovocytaire. Réagit à l'alimentation, à l'activité physique, à l'inositol ou à la metformine en 2 à 3 mois.",
        retestInterval: "3 mois",
      },
      {
        name: "HbA1c",
        expectedToChange: true,
        expectation:
          "Reflète la glycémie moyenne sur 3 mois. Cible : < 5,7 %. Réagit aux ajustements alimentaires et à la gestion de la glycémie.",
        retestInterval: "3 mois",
      },
      {
        name: "CRP ultrasensible",
        expectedToChange: true,
        expectation:
          "Marqueur d'inflammation systémique lié aux échecs d'implantation et aux complications de grossesse. Réagit à une alimentation anti-inflammatoire et aux oméga-3 en 8 à 12 semaines.",
        retestInterval: "3 mois",
      },
      {
        name: "Homocystéine",
        expectedToChange: true,
        expectation:
          "Des taux élevés sont liés aux fausses couches à répétition et aux anomalies du tube neural. Bonne réponse à la supplémentation en méthylfolate, B12 et B6.",
        retestInterval: "8 semaines",
      },
      {
        name: "Vitamine D",
        expectedToChange: true,
        expectation:
          "Le déficit est associé à de moins bons résultats en FIV et à des complications de grossesse. Cible : > 40 ng/mL. Corrigeable en 8 semaines avec des compléments.",
        retestInterval: "8 semaines",
      },
      {
        name: "Ferritine",
        expectedToChange: true,
        expectation:
          "Les réserves en fer influent sur l'énergie, la qualité ovocytaire et la grossesse. Cible : > 40 ng/mL. Réagit aux compléments sur 3 à 6 mois.",
        retestInterval: "3 mois",
      },
      {
        name: "TSH",
        expectedToChange: true,
        expectation:
          "Cible fertilité : < 2,5 mUI/L. Si elle est élevée ou si les anticorps anti-TPO sont positifs, un traitement la corrige généralement en 6 à 8 semaines.",
        retestInterval: "6 à 8 semaines",
      },
      {
        name: "Prolactine",
        expectedToChange: true,
        expectation:
          "Une prolactine élevée peut bloquer l'ovulation. Se traite avec de la cabergoline. Les taux se normalisent en quelques semaines.",
        retestInterval: "4 à 6 semaines",
      },
      {
        name: "DHEA-S",
        expectedToChange: false,
        expectation:
          "Une DHEA-S basse chez les femmes avec réserve ovarienne diminuée peut justifier une supplémentation. Référence importante pour le protocole de FIV.",
      },
      {
        name: "Anticorps antiphospholipides",
        expectedToChange: false,
        expectation:
          "Dépiste le syndrome des antiphospholipides — une cause majeure de fausses couches à répétition. Si positif, à confirmer à 12 semaines ; peut justifier aspirine ou héparine en grossesse.",
      },
      {
        name: "Facteur V Leiden / MTHFR",
        expectedToChange: false,
        expectation:
          "Dépistage des thrombophilies génétiques. Ne change pas, mais oriente la décision d'introduire des anticoagulants en grossesse et en cycle de FIV.",
      },
      {
        name: "ApoB",
        expectedToChange: true,
        expectation:
          "Meilleur indicateur du risque cardiovasculaire que le LDL seul. Référence importante avant grossesse, qui sollicite fortement le système cardiovasculaire.",
        retestInterval: "6 mois",
      },
    ],
    steps: [
      {
        title: "Prise de sang à jeun",
        description:
          "Plus de 100 biomarqueurs prélevés en une seule prise de sang matinale à jeun, via notre laboratoire partenaire. On coordonne tout — une visite, un prélèvement.",
      },
      {
        title: "Progestérone en milieu de phase lutéale",
        description:
          "Prélèvement séparé 7 jours après l'ovulation (vers le 21e jour) pour confirmer l'ovulation. Passage rapide au laboratoire ou kit à domicile.",
      },
      {
        title: "Interprétation orientée fertilité",
        description:
          "Séance de 45 minutes avec un·e praticien·ne spécialisé·e en fertilité. On ne te livre pas juste 100 chiffres — chaque marqueur est relié à ton projet reproductif.",
      },
    ],
  },
});

frModule("module-comprehensive-genomic", {
  title: "Profil génomique",
  subtitle:
    "Séquençage de l'exome ou du génome avec pharmacogénomique — bien au-delà du dépistage des porteurs sains",
  body: {
    includes: [
      "Séquençage de l'exome (WES) ou du génome (WGS) pour chaque partenaire",
      "Panel pharmacogénomique — comment tu métabolises les médicaments de fertilité (létrozole, gonadotrophines, progestérone)",
      "Scores de risque polygénique pour les conditions liées à la fertilité (PMOS (ex SOPK), endométriose, insuffisance ovarienne prématurée, fausses couches à répétition)",
      "Dépistage des variants monogéniques au-delà des panels standard (variants rares, revue des VSI)",
      "Séance approfondie avec un·e conseiller·ère en génétique pour interpréter les résultats complexes",
      "Recommandations concrètes sur les médicaments et la réponse au traitement selon ton génome",
    ],
    trackableMarkers: [
      {
        name: "Profil pharmacogénomique",
        expectedToChange: false,
        expectation:
          "Ton profil génétique de réponse aux médicaments est fixe — mais il change la façon dont ta médecin dose le létrozole, les gonadotrophines et les autres traitements. Identifié une fois, utile pour chaque cycle à venir.",
      },
      {
        name: "Scores de risque polygénique",
        expectedToChange: false,
        expectation:
          "Scores de risque pour le PMOS (ex SOPK), l'endométriose, l'IOP et les fausses couches à répétition. Ils ne changent pas, mais ils orientent un dépistage et un plan de traitement plus proactifs.",
      },
      {
        name: "Variants monogéniques",
        expectedToChange: false,
        expectation:
          "Variants rares pouvant affecter la fertilité, l'implantation ou le déroulement d'une grossesse. Va au-delà du dépistage standard des porteurs sains pour identifier ce qu'un panel classique manquerait.",
      },
    ],
    steps: [
      {
        title: "Recueil du prélèvement",
        description:
          "Prise de sang ou kit salivaire pour chaque partenaire. Réalisable au laboratoire ou à domicile avec un kit à renvoyer par voie postale.",
      },
      {
        title: "Séquençage",
        description:
          "Séquençage de l'exome ou du génome entier. Le délai est de 4 à 8 semaines selon le laboratoire et la profondeur du séquençage.",
      },
      {
        title: "Analyse bio-informatique",
        description:
          "Profil pharmacogénomique, scores de risque polygénique et interprétation des variants par une équipe de génétique clinique.",
      },
      {
        title: "Conseil génétique",
        description:
          "Séance de 60 minutes avec un·e conseiller·ère en génétique pour parcourir l'ensemble des résultats — réponse médicamenteuse, scores de risque et tout variant exploitable.",
      },
    ],
  },
});

frModule("module-genetic-carrier", {
  title: "Dépistage des porteurs sains",
  subtitle:
    "Panel de dépistage des maladies héréditaires — pas un génome complet, mais incontournable pour planifier une famille",
  body: {
    includes: [
      "Panel élargi de dépistage des porteurs sains (200+ maladies) par prise de sang ou salive",
      "Dépistage des deux partenaires pour les maladies récessives",
      "Consultation avec un·e conseiller·ère en génétique pour revoir les résultats",
      "Conduite à tenir si les deux partenaires sont porteurs de la même maladie",
      "Implications pour le DPI-M (diagnostic préimplantatoire pour les maladies monogéniques)",
    ],
    steps: [
      {
        title: "Recueil du prélèvement",
        description:
          "Prise de sang simple ou kit salivaire pour chaque partenaire. Réalisable au laboratoire ou à domicile.",
      },
      {
        title: "Analyse en laboratoire",
        description:
          "L'analyse génétique prend généralement 2 à 4 semaines. Dépiste la mucoviscidose, l'amyotrophie spinale, le syndrome de l'X fragile et plus de 200 maladies.",
      },
      {
        title: "Résultats et conseil",
        description:
          "Un·e conseiller·ère en génétique passe en revue les résultats. Si les deux partenaires sont porteurs de la même maladie, on aborde le DPI-M et les autres options.",
      },
    ],
  },
});

// ─── Optimization ───────────────────────────────────────────────────

frModule("module-female-optimization", {
  title: "Optimisation de la qualité ovocytaire",
  subtitle: "Protocole de 90 jours pour soutenir tes ovocytes en maturation",
  body: {
    includes: [
      "Protocole ciblé de compléments (CoQ10 400 à 600 mg, vitamine D, oméga-3, méthylfolate)",
      "Évaluation de la DHEA en cas de réserve ovarienne diminuée",
      "Recommandations sur le mode de vie (sommeil, activité physique, gestion du stress)",
      "Questionnaires de suivi mensuels",
      "Bilan sanguin du 3e jour à 90 jours pour suivre les tendances",
    ],
    trackableMarkers: [
      {
        name: "Vitamine D",
        expectedToChange: true,
        expectation:
          "Corrigeable avec des compléments. Contrôle à 8 semaines pour confirmer un taux adéquat (> 40 ng/mL).",
        retestInterval: "8 semaines",
      },
      {
        name: "TSH",
        expectedToChange: true,
        expectation:
          "Si elle est élevée, un traitement permet de la normaliser. Cible fertilité : < 2,5.",
        retestInterval: "6 à 8 semaines après le début du traitement",
      },
      {
        name: "AMH",
        expectedToChange: false,
        expectation:
          "L'AMH reflète ton stock folliculaire et décline généralement avec l'âge. On la suit pour comprendre ta trajectoire, pas pour mesurer une amélioration.",
        retestInterval: "6 mois",
      },
    ],
    steps: [
      {
        title: "Démarrer les compléments",
        description:
          "Commence la CoQ10, la vitamine D, les oméga-3 et le méthylfolate. Il faut environ 3 mois pour qu'ils agissent sur les ovocytes en maturation.",
      },
      {
        title: "Ajustements du mode de vie",
        description:
          "7 à 9 heures de sommeil, activité physique modérée, gestion du stress. Ils façonnent l'environnement hormonal dans lequel tes ovocytes se développent.",
      },
      {
        title: "Suivi mensuel",
        description:
          "Questionnaire rapide sur l'énergie, le sommeil et les effets secondaires. Protocole ajusté si besoin.",
      },
      {
        title: "Bilan à 90 jours",
        description:
          "Nouveau panel hormonal. La vitamine D et la thyroïde devraient être optimisées. L'AMH est suivie pour visualiser la tendance.",
      },
    ],
  },
});

frModule("module-male-optimization", {
  title: "Optimisation du sperme",
  subtitle:
    "Protocole de 90 jours basé sur les preuves pour une amélioration mesurable du sperme",
  body: {
    includes: [
      "Stack ciblé de compléments (CoQ10, zinc, vitamine D, oméga-3)",
      "Protocole de mode de vie (éviter la chaleur, activité physique, sommeil, réduction de l'alcool)",
      "Points mensuels avec suivi des progrès",
      "Nouveau spermogramme à 90 jours pour mesurer l'amélioration",
      "En option : test de fragmentation de l'ADN pour aller plus loin",
    ],
    trackableMarkers: [
      {
        name: "Numération",
        expectedToChange: true,
        expectation:
          "Réagit nettement à l'évitement de la chaleur, à la réduction de l'alcool et aux compléments. Changement mesurable attendu à 90 jours.",
        retestInterval: "90 jours",
      },
      {
        name: "Mobilité",
        expectedToChange: true,
        expectation:
          "Souvent le premier paramètre à s'améliorer. La mobilité progressive doit être > 32 % (référence OMS).",
        retestInterval: "90 jours",
      },
      {
        name: "Fragmentation de l'ADN",
        expectedToChange: true,
        expectation:
          "Réagit aux antioxydants et aux changements de mode de vie. Ne fait pas partie du spermogramme standard — à demander séparément.",
        retestInterval: "90 jours",
      },
      {
        name: "Morphologie",
        expectedToChange: true,
        expectation:
          "Paramètre le moins réactif, mais qui peut évoluer modérément. Beaucoup de spécialistes se concentrent davantage sur la numération et la mobilité.",
        retestInterval: "90 jours",
      },
    ],
    steps: [
      {
        title: "Démarrer les compléments",
        description:
          "CoQ10 (200 mg), zinc (30 mg), vitamine D (2000 à 4000 UI), oméga-3. On vise le cycle complet de 74 jours de spermatogenèse.",
      },
      {
        title: "Changements de mode de vie",
        description:
          "Éviter bains chauds et saunas, ordinateur sur les genoux, sous-vêtements serrés. Réduire l'alcool. Activité physique modérée.",
      },
      {
        title: "Suivi mensuel",
        description:
          "Suivi de l'observance, de l'énergie et des effets secondaires éventuels. Ajustement du protocole si besoin.",
      },
      {
        title: "Bilan à 90 jours",
        description:
          "Nouveau spermogramme pour mesurer les évolutions de numération, mobilité et morphologie par rapport à ton point de départ.",
      },
    ],
  },
});

// ─── Treatment ──────────────────────────────────────────────────────

frModule("module-ovarian-stimulation", {
  title: "Stimulation ovarienne",
  subtitle: "10 à 14 jours de stimulation contrôlée avec suivi",
  body: {
    includes: [
      "Protocole quotidien de médicaments injectables (FSH/LH)",
      "Échographies de suivi toutes les 2 à 3 jours",
      "Prises de sang d'œstradiol pour suivre la croissance folliculaire",
      "Accompagnement aux injections avec vidéos tutoriels",
      "Ajustement des doses en fonction de la réponse",
      "Coordination du déclenchement",
    ],
    trackableMarkers: [
      {
        name: "Nombre de follicules en croissance",
        expectedToChange: true,
        expectation:
          "Les follicules grandissent d'environ 1 à 2 mm par jour pendant la stimulation. Le suivi indique combien arrivent à maturité et à quelle taille.",
      },
      {
        name: "Œstradiol (E2)",
        expectedToChange: true,
        expectation:
          "Un E2 en hausse confirme que les follicules répondent. Chaque follicule mature contribue à environ 200 à 300 pg/mL.",
      },
    ],
    steps: [
      {
        title: "Commencer les injections",
        description:
          "Démarrage des injections quotidiennes de gonadotrophines. Premier rendez-vous de suivi 4 à 5 jours plus tard.",
      },
      {
        title: "Suivi",
        description:
          "Échographie + prise de sang toutes les 2 à 3 jours. Doses ajustées selon la croissance folliculaire et le taux d'E2.",
      },
      {
        title: "Déclenchement",
        description:
          "Quand les follicules en tête atteignent 18 à 20 mm, le déclenchement est administré. Ponction programmée 36 heures plus tard.",
      },
    ],
  },
});

frModule("module-egg-retrieval", {
  title: "Ponction ovocytaire",
  subtitle: "Intervention ambulatoire pour recueillir les ovocytes matures",
  body: {
    includes: [
      "Consignes de préparation à l'intervention (jeûne, heure d'arrivée)",
      "Ponction sous sédation légère (~ 20 minutes)",
      "Surveillance en salle de réveil (1 à 2 heures)",
      "Compte d'ovocytes communiqué le jour même",
      "Guide de récupération post-ponction (ballonnements, crampes)",
    ],
    steps: [
      {
        title: "Avant l'intervention",
        description:
          "Rien par voie orale après minuit. Arriver 30 minutes en avance. Prévoir une personne pour le retour.",
      },
      {
        title: "Ponction",
        description:
          "Aspiration à l'aiguille sous guidage échographique et sédation. Généralement 15 à 20 minutes.",
      },
      {
        title: "Récupération",
        description:
          "Repos en salle de réveil pendant 1 à 2 heures. Compte d'ovocytes communiqué le jour même. Prévoir 1 à 2 jours de repos.",
      },
    ],
  },
});

frModule("module-fertilization-culture", {
  title: "Fécondation et culture",
  subtitle:
    "Fécondation et développement embryonnaire jusqu'au stade blastocyste",
  body: {
    includes: [
      "Préparation et sélection des spermatozoïdes",
      "Fécondation par FIV classique ou ICSI",
      "Compte rendu de fécondation au jour 1 (nombre d'ovocytes fécondés)",
      "Point de développement au jour 3 (nombre de cellules, qualité)",
      "Compte rendu blastocyste au jour 5 ou 6 (nombre ayant atteint ce stade, qualité)",
      "Cryoconservation des blastocystes viables",
    ],
    steps: [
      {
        title: "Fécondation (Jour 0)",
        description:
          "Ovocytes et spermatozoïdes sont mis en contact par FIV classique ou ICSI. Résultats communiqués le lendemain matin.",
      },
      {
        title: "Développement précoce (Jour 1 à 3)",
        description:
          "Les ovocytes fécondés (zygotes) commencent à se diviser. Point au jour 3 sur le nombre de cellules et la qualité.",
      },
      {
        title: "Stade blastocyste (Jour 5 à 6)",
        description:
          "Les embryons ayant atteint le stade blastocyste sont notés puis congelés ou préparés pour le transfert.",
      },
    ],
  },
});

frModule("module-pgt-a", {
  title: "Dépistage PGT-A",
  subtitle: "Test génétique préimplantatoire pour les anomalies chromosomiques",
  body: {
    includes: [
      "Biopsie du trophectoderme sur les embryons au stade blastocyste",
      "Séquençage de nouvelle génération sur l'ensemble des 23 paires de chromosomes",
      "Classification euploïde / aneuploïde / mosaïque pour chaque embryon",
      "Compte rendu avec recommandations de transfert",
      "Consultation avec un·e conseiller·ère en génétique pour revoir les résultats",
    ],
    steps: [
      {
        title: "Biopsie",
        description:
          "Quelques cellules sont prélevées sur la couche externe (trophectoderme) de chaque blastocyste. Les embryons sont congelés pendant l'analyse.",
      },
      {
        title: "Analyse génétique",
        description:
          "Les échantillons sont envoyés en laboratoire de génétique. Résultats généralement disponibles sous 7 à 10 jours ouvrés.",
      },
      {
        title: "Restitution",
        description:
          "Chaque embryon est classé euploïde (chromosomes normaux), aneuploïde ou mosaïque. Cela oriente les décisions de transfert.",
      },
    ],
  },
});

frModule("module-embryo-transfer", {
  title: "Transfert d'embryon",
  subtitle: "Transfert d'un embryon unique avec suivi établi par le centre",
  body: {
    includes: [
      "Préparation endométriale selon les consignes du centre",
      "Coordination du jour du transfert consignée par l'équipe soignante",
      "Détails du geste et documents sources",
      "Tests prescrits et consignes de médicaments communiqués par l'équipe soignante",
    ],
    trackableMarkers: [
      {
        name: "Évaluation de l'endomètre",
        expectedToChange: false,
        expectation:
          "Le centre interprète la préparation selon le protocole, l'imagerie, les analyses, le calendrier et les antécédents ; il n'existe pas de règle universelle d'épaisseur ou d'aspect.",
      },
      {
        name: "Test de suivi prescrit",
        expectedToChange: false,
        expectation:
          "L'équipe consigne le test et le suivi prescrits. Un seul résultat ne suffit pas à établir à lui seul la localisation ou la viabilité d'une grossesse.",
      },
    ],
    steps: [
      {
        title: "Préparation endométriale",
        description:
          "Le centre choisit la préparation, le suivi et le calendrier du transfert.",
      },
      {
        title: "Transfert",
        description:
          "L'expérience du geste et l'imagerie varient ; l'équipe consigne ce qui s'est passé et si le transfert a eu lieu.",
      },
      {
        title: "Soutien lutéal",
        description:
          "Suis uniquement les consignes de médicaments et de suivi prescrites par l'équipe soignante.",
      },
    ],
  },
});

frModule("module-iui-procedure", {
  title: "IIU",
  subtitle: "Insémination intra-utérine avec sperme préparé",
  body: {
    includes: [
      "Suivi de l'ovulation (échographie + détection du pic LH)",
      "Timing du déclenchement en cas de cycle médicalisé",
      "Préparation du sperme",
      "Insémination intra-utérine (~ 5 minutes)",
      "Compte rendu du sperme après préparation",
    ],
    trackableMarkers: [
      {
        name: "Numération après préparation",
        expectedToChange: false,
        expectation:
          "Nombre total de spermatozoïdes mobiles après préparation. > 10 millions de mobiles est l'idéal pour une IIU.",
      },
      {
        name: "Taille du follicule au déclenchement",
        expectedToChange: true,
        expectation:
          "Le follicule en tête doit mesurer 18 à 24 mm au moment du déclenchement. Cela cale le timing de l'ovulation.",
      },
    ],
    steps: [
      {
        title: "Suivi",
        description:
          "Échographie pour confirmer la maturité du follicule en tête. Déclenchement si traitement médicamenteux en place.",
      },
      {
        title: "Préparation du prélèvement",
        description:
          "Recueil du sperme, puis préparation pour concentrer les spermatozoïdes mobiles. Environ 1 heure.",
      },
      {
        title: "Insémination",
        description:
          "Un fin cathéter dépose le sperme préparé directement dans l'utérus. Geste rapide et généralement indolore.",
      },
    ],
  },
});

// ─── Support ────────────────────────────────────────────────────────

frModule("module-tww-support", {
  title: "Accompagnement période d'attente",
  subtitle:
    "Accompagnement émotionnel et pratique guidé pendant la période d'attente",
  body: {
    includes: [
      "Méditations guidées et propositions d'écriture quotidiennes",
      "Suivi des symptômes sans tomber dans la spirale (conseils encadrés)",
      "Groupe de pairs avec d'autres personnes dans la même période d'attente",
      "Calendrier de tests et interprétation des résultats",
      "Stratégies d'adaptation, quel que soit le résultat",
    ],
    trackableMarkers: [
      {
        name: "Bêta-hCG",
        expectedToChange: true,
        expectation:
          "Prise de sang 9 à 11 jours après le transfert (ou 14 jours après l'IIU). La réponse définitive.",
        retestInterval: "48 heures si positif (pour confirmer le doublement)",
      },
    ],
    steps: [
      {
        title: "Jours 1 à 5",
        description:
          "Repos et récupération. Mouvement doux, hydratation et gestion du stress.",
      },
      {
        title: "Jours 6 à 10",
        description:
          "La période la plus dure. Méditations et propositions d'écriture quotidiennes pour rester ancrée.",
      },
      {
        title: "Jours 11 à 14",
        description:
          "Recommandations pour le test et interprétation du résultat. Accompagnement, quel que soit l'issue.",
      },
    ],
  },
});
